import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { ChevronLeft, ChevronRight, FileText, Info, Loader2, Play, Home } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

 

// Form Details Modal Component
const FormDetailsModal = ({ selectedForm, auditFormData, isLoadingFormData, formDataError }) => {
  const { t } = useTranslation();
  if (isLoadingFormData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>{t('category.loading_form_details')}</span>
      </div>
    );
  }

  if (formDataError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-2">{t('category.error_loading_details')}</div>
        <div className="text-sm text-gray-500">{formDataError}</div>
      </div>
    );
  }

  if (!auditFormData?.data) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">{t('category.no_detailed_data')}</div>
      </div>
    );
  }

 

  const formDetails = auditFormData.data;

  return (
    <ScrollArea className="max-h-[60vh] pr-4">
      <div className="space-y-4">
        {/* Basic Form Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.form_name')}</label>
            <p className="text-lg font-semibold">{formDetails.form_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.form_code')}</label>
            <p className="text-lg font-semibold">{formDetails.form_code || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.italian_name')}</label>
            <p className="text-lg font-semibold">{formDetails.form_name_italian || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.total_fields')}</label>
            <p className="text-lg font-semibold">{formDetails.total_fields_count || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.total_points')}</label>
            <p className="text-lg font-semibold">{formDetails.total_possible_points || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.passing_threshold')}</label>
            <p className="text-lg font-semibold">{formDetails.passing_threshold || 0}%</p>
          </div>
        </div>

        {/* Production Line Information */}
        {formDetails.production_line && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">{t('category.production_line')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.line_name')}</label>
                <p className="font-medium">{formDetails.production_line.line_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.line_id')}</label>
                <p className="font-medium">{formDetails.production_line.line_id || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Product Information */}
        {formDetails.product && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">{t('category.product')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.product_name')}</label>
                <p className="font-medium">{formDetails.product.product_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.model_number')}</label>
                <p className="font-medium">{formDetails.product.model_number || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        {formDetails.form_structure && formDetails.form_structure.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">{t('category.categories')} ({formDetails.form_structure.length})</h3>
            <div className="space-y-2">
              {formDetails.form_structure.map((category, index) => (
                <div key={category.id || index} className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{category.title}</p>
                      {category.categoryData?.category_code_it && (
                        <p className="text-sm text-gray-600">{category.categoryData.category_code_it}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="ml-2">
                        {category.fields?.length || 0} {t('category.items')}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status and Dates */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-2">{t('category.status_dates')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <Badge variant={formDetails.is_published ? "default" : "secondary"}>
                {formDetails.is_published ? "Published" : "Draft"}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('category.created')}</label>
              <p className="font-medium">
                {formDetails.created_at ? new Date(formDetails.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

// CategoryViewPage Component
export default function CategoryViewPage({
  onPrevious,
  onNext,
  onCategoryClick,
  onNavigate,
  onStartFullAudit, // NEW: Handler for starting full audit
  selectedForm,
  auditFormData,
  isLoadingFormData,
  formDataError,
  currentAuditResponse,  // NEW: For progress tracking
  isCreatingNewAudit = false, // Flag to indicate if this is a new audit
  isResumedFromDraft = false, // Flag to indicate if this audit was resumed from dashboard
  // Score props
  currentScore = null,
  showScore = false,
}) {
  const { t } = useTranslation();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  // Should show Dashboard button only for resumed drafts (not new audits)
  const shouldShowDashboard = isResumedFromDraft && currentAuditResponse?.status === 'DRAFT';

  // Debug logging (reduced frequency)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Category Page - Props received:', {
        hasCurrentAuditResponse: !!currentAuditResponse,
        auditResponseId: currentAuditResponse?.audit_form_response_id,
        hasCumulativeData: !!currentAuditResponse?.raw_response_json?.cumulativeAuditData
      });
      console.log('🔍 Category Page - Button Logic:', {
        isCreatingNewAudit,
        isResumedFromDraft,
        status: currentAuditResponse?.status,
        shouldShowDashboard
      });
    }
  }, [currentAuditResponse?.audit_form_response_id, isResumedFromDraft]);
  // Generate categories from auditFormData with real progress data and resume points
  const generateCategories = () => {

    if (!auditFormData?.data?.form_structure) {
      // Fallback categories if no form data
      return [
        { id: 1, name: "DOCUMENTAZIONE", translation: "Documentation", status: "Not Started", completed: 0, total: 0, resumePoint: null },
        { id: 2, name: "ETICHETTE", translation: "Labels", status: "Not Started", completed: 0, total: 0, resumePoint: null }
      ];
    }

    console.log('🔍 CategoryViewPage - generateCategories DEBUG:', {
      hasCurrentAuditResponse: !!currentAuditResponse,
      hasCumulativeData: !!currentAuditResponse?.raw_response_json?.cumulativeAuditData,
      cumulativeDataKeys: Object.keys(currentAuditResponse?.raw_response_json?.cumulativeAuditData || {})
    });

    return auditFormData.data.form_structure.map((category, index) => {
      const categoryId = category.categoryData?.category_id;
      const savedCategoryData = currentAuditResponse?.raw_response_json?.cumulativeAuditData?.[categoryId];
      const categoryProgress = currentAuditResponse?.raw_response_json?.categoryProgressMap?.[categoryId];

      console.log(`🔍 Category ${categoryId}:`, {
        hasSavedData: !!savedCategoryData,
        controlItemsCount: savedCategoryData?.controlItems ? Object.keys(savedCategoryData.controlItems).length : 0
      });

      // Get control items count and list from hierarchy
      const categoryControlItems = auditFormData?.hierarchy?.control_items
        ?.filter(ci => ci.category_id === categoryId) || [];
      const totalControlItems = categoryControlItems.length;

      // Calculate completion status dynamically by checking actual control item data
      let completedControlItems = 0;
      if (savedCategoryData?.controlItems) {
        Object.values(savedCategoryData.controlItems).forEach(controlItem => {
          // A control item is completed if it has at least one checked check
          const checks = Object.values(controlItem.checks || {});
          const hasCompletedChecks = checks.some(check => check.isChecked === true);
          if (hasCompletedChecks) {
            completedControlItems++;
          }
        });
      }

      // Calculate status based on actual completion count
      let status = "Not Started";
      if (completedControlItems > 0) {
        status = completedControlItems === totalControlItems ? "Completed" : "In Progress";
      }

      // Determine resume point for in-progress categories
      let resumePoint = null;
      if (status === "In Progress" && categoryProgress && totalControlItems > 0) {
        const currentControlItemIndex = categoryProgress.currentControlItemIndex || 0;
        const resumeControlItem = categoryControlItems[currentControlItemIndex];
        if (resumeControlItem) {
          resumePoint = {
            controlItemName: resumeControlItem.control_item_code_it || resumeControlItem.control_item_code,
            controlItemIndex: currentControlItemIndex + 1,
            totalControlItems: totalControlItems
          };
        }
      }

      // Extract Italian translation from categoryData if available
      const translation = category.categoryData?.category_code_it ||
                         category.title.split(' / ')[1] || // Try to extract from title
                         "";

      return {
        id: categoryId || index + 1,
        name: category.categoryData?.category_code || category.title.split(' / ')[0] || category.title,
        translation: translation,
        status: status,
        completed: completedControlItems,
        total: totalControlItems,
        resumePoint: resumePoint, // Add resume point information
        lastVisited: categoryProgress?.lastVisited || null,
        originalCategory: category // Keep reference to original data
      };
    });
  };

  const categories = generateCategories();

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return "bg-green-100 text-green-700 border-green-200";
      case "In Progress": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "Not Started": return "bg-gray-100 text-gray-700 border-gray-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case "Completed": return "text-green-700";
      case "In Progress": return "text-yellow-700";
      case "Not Started": return "text-gray-700";
      default: return "text-gray-700";
    }
  };

  

  return (
    <div className="pwa-flex-page bg-gray-50 mt-8">
      {/* Fixed Header */}
      <header className="pwa-fixed-header bg-white px-6 py-6 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="text-center md:text-left">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('category.audit_session')}</span>
              <strong className="block text-lg font-bold text-gray-900 mt-1">
                {currentAuditResponse?.audit_form_response_id ? `AUD-${currentAuditResponse.audit_form_response_id}` : 'AUD-NEW'}
              </strong>
            </div>
            <div className="text-center">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('category.work_started')}</span>
              <strong className="block text-lg font-bold text-gray-900 mt-1">
                {currentAuditResponse?.created_at 
                  ? new Date(currentAuditResponse.created_at).toLocaleString('en-GB', {
                      year: 'numeric',
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }).replace(',', '')
                  : new Date().toLocaleString('en-GB', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit', 
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }).replace(',', '')
                }
              </strong>
            </div>
            <div className="text-center md:text-right">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('category.product_serial')}</span>
              <strong className="block text-lg font-bold text-gray-900 mt-1">
                {currentAuditResponse?.serial_number || selectedForm?.serial_number || 'N/A'}
              </strong>
            </div>
          </div>
        </div>
      </header>

      {/* Fixed Title Section with Score */}
      <section className="bg-sky-500 px-6 py-4 shadow-lg flex-shrink-0" style={{ background: "#0098cb" }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white text-center md:text-left flex-shrink-0">{t('category.title')}</h1>

          {/* Center: Score Display */}
          {showScore && currentScore && (
            <div className="flex-1 flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                <div className="text-center">
                  <span className="text-xs font-medium text-white/80 uppercase tracking-wider">{t('category.current_score')}</span>
                  <div className="mt-0.5 flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-bold text-white">{currentScore.achieved}</span>
                    <span className="text-xs text-white/70">{t('category.points')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Right: Form Details Button */}
          {selectedForm && (
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 flex-shrink-0"
              onClick={() => setIsFormModalOpen(true)}
            >
              <Info className="h-4 w-4 mr-2" />
              {t('category.form_details')}
            </Button>
          )}
        </div>
      </section>

      {/* Fixed Action Section */}
      <div className="bg-white px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Display selected form info */}
          {/* {selectedForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-900">Selected Form: {selectedForm.title}</h3>
                  <p className="text-sm text-blue-700">{selectedForm.description}</p>
                  {selectedForm.form_code && (
                    <p className="text-xs text-blue-600">Code: {selectedForm.form_code}</p>
                  )}
                </div>
                {isLoadingFormData && (
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                )}
              </div>
            </div>
          )} */}

          {/* Start Full Audit Button */}
          {!isLoadingFormData && categories.length > 0 && onStartFullAudit && (
            <Button
              onClick={() => onStartFullAudit(categories[0])}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
            >
              <Play className="h-5 w-5" />
              {t('category.start_complete_audit')}
              <span className="text-sm opacity-90">{t('category.all_categories')}</span>
            </Button>
          )}

          {/* Individual Category Selection Header */}
          {!isLoadingFormData && categories.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{t('category.select_individual')}</h2>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Main Content - THIS IS THE KEY CHANGE */}
      <div className="pwa-scrollable-content pwa-footer-clearance">
        <div className="h-full">
          <div className="pwa-tablet-container max-w-2xl mx-auto h-full">
            <div className="h-full pwa-content-padding pwa-no-horizontal-scroll">
              {/* Loading state for categories */}
              {isLoadingFormData && !auditFormData && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                    <span className="text-gray-600">{t('category.loading_categories')}</span>
                  </div>
                </div>
              )}

              {/* Error state */}
              {formDataError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="text-red-800 font-medium">{t('category.error_loading_form')}</div>
                  <div className="text-red-600 text-sm mt-1">{formDataError}</div>
                </div>
              )}

              {/* Categories List - SCROLLABLE CONTENT */}
              {!isLoadingFormData && categories.length > 0 && (
                <div className="h-full overflow-y-auto">
                  <div className="space-y-4 pb-20">
                    {categories.map((category, index) => (
                      <button
                        key={category.id}
                        type="button"
                        className={cn(
                          "w-full text-left bg-white rounded-lg shadow-sm border border-gray-200 p-6",
                          "transition-all duration-200 outline-none cursor-pointer opacity-0 animate-[slideInStaggered_0.3s_ease-out_forwards]",
                          "hover:bg-sky-100 hover:border-sky-400 hover:shadow-md",
                          "focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
                        )}
                        style={{ animationDelay: `${index * 0.05}s` }}
                        onClick={() => onCategoryClick?.(category)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 mb-2">{category.translation}</h2>
                            {category.name && <p className="text-gray-600 text-sm">{category.name}</p>}

                            {/* Resume Point Information */}
                            {category.resumePoint && (
                              <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                                <div className="flex items-center gap-2">
                                  <Play className="h-3 w-3 text-blue-600" />
                                  <span className="text-xs font-medium text-blue-800">Resume from:</span>
                                </div>
                                <p className="text-xs text-blue-700 mt-1">
                                  Item {category.resumePoint.controlItemIndex} of {category.resumePoint.totalControlItems}: {category.resumePoint.controlItemName}
                                </p>
                              </div>
                            )}

                            {/* Last Visited */}
                            {category.lastVisited && (
                              <p className="text-xs text-gray-500 mt-1">
                                Last visited: {new Date(category.lastVisited).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge variant="outline" className={cn("px-3 py-1 rounded-full font-medium", getStatusColor(category.status))}>
                              {category.status}
                            </Badge>
                            <span className={cn("text-sm font-medium", getProgressColor(category.status))}>
                              {category.completed}/{category.total} {t('category.control_items_completed')}
                            </span>

                            {/* Progress Bar */}
                            {category.total > 0 && (
                              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full transition-all duration-300",
                                    category.status === "Completed" ? "bg-green-500" :
                                    category.status === "In Progress" ? "bg-blue-500" : "bg-gray-300"
                                  )}
                                  style={{
                                    width: `${Math.round((category.completed / category.total) * 100)}%`
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isLoadingFormData && !formDataError && categories.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
                    <h3 className="text-lg font-medium mb-2">{t('category.no_categories')}</h3>
                    <p className="text-sm">{t('category.no_categories_desc')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <footer className="pwa-fixed-footer pwa-footer-padding bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto flex justify-between items-center pwa-footer-button-spacing">
          {shouldShowDashboard ? (
            // For resumed draft audits: Show Dashboard button
            <Button
              variant="outline"
              onClick={() => onNavigate && onNavigate('chillerAuditDashboard')}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              {t('btn.dashboard')}
            </Button>
          ) : (
            // For new audits: Show Previous button
            <Button variant="outline" onClick={onPrevious} className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              {t('btn.previous')}
            </Button>
          )}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => {
              console.log('🔥 CategoryViewPage - Summary button clicked!', { onNavigate: !!onNavigate });
              if (onNavigate) {
                console.log('🔥 Calling onNavigate with auditSummary');
                onNavigate('auditSummary');
              } else {
                console.error('❌ onNavigate is not defined!');
              }
            }} className="flex items-center gap-2">
              {t('nav.summary')}
            </Button>
            <Button onClick={onNext} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
              {t('btn.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </footer>

      {/* Form Details Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedForm?.title} - Details
            </DialogTitle>
          </DialogHeader>
          <FormDetailsModal 
            selectedForm={selectedForm}
            auditFormData={auditFormData}
            isLoadingFormData={isLoadingFormData}
            formDataError={formDataError}
          />
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes slideInStaggered {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}